import stripAnsi from "strip-ansi";
export const isOrdered = (node) => {
    return "value" in node;
};
const TOKEN_TO_MARKUP = {
    "**": "strong",
    "*": "em",
    "`": "code"
};
function getMarkupTypeFromToken(token) {
    if (token.length > 2 && token.startsWith("<")) {
        return "xml";
    }
    return TOKEN_TO_MARKUP[token];
}
const MARKUP_TO_TOKEN = {
    strong: "**",
    em: "*",
    code: "`"
};
function cleanTextNode(x) {
    return stripAnsi(x).replace("&amp;", "&");
}
export const parseMarkdownLine = (blocks) => {
    const nodes = [];
    const scopes = [];
    const addNode = (node) => {
        if (scopes.length > 0) {
            const lastScope = scopes[scopes.length - 1];
            if (lastScope.type === "xml") {
                lastScope.children.push(typeof node === "string" || node.type !== "xml" ? { type: "span", children: [node] } : node);
            }
            else {
                lastScope.children.push(node);
            }
        }
        else {
            nodes.push(node);
        }
    };
    const popLastScope = () => {
        const element = scopes.pop();
        if (element !== undefined) {
            addNode(element);
        }
    };
    let match;
    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < blocks.length; i++) {
        const chunk = blocks[i];
        if (typeof chunk !== "string") {
            // let's just assume for now highlights don't have markdown
            addNode({
                type: "data",
                data: chunk
            });
            continue;
        }
        // const formatRe = /<([a-z]+)>|<\/([a-z]+)>|\\|\*\*|\*`/g;
        const formatRe = /\\|\*\*|\*|`/g;
        let lastIndex = 0;
        while ((match = formatRe.exec(chunk)) != null) {
            const block = chunk.substring(lastIndex, match.index);
            if (block.length > 0) {
                addNode(cleanTextNode(block));
            }
            lastIndex = match.index + match[0].length;
            if (match[0] == "\\") {
                formatRe.lastIndex = lastIndex + 1;
            }
            else {
                const type = getMarkupTypeFromToken(match[0]);
                if (type === undefined) {
                    continue;
                }
                else if (type === "xml") {
                    const closed = match[0].startsWith("</");
                    const tag = match[0].substring(closed ? 2 : 1, match[0].length - 1);
                    const lastScope = scopes[scopes.length - 1];
                    if (lastScope === undefined || lastScope.type !== "xml" || lastScope.tag !== tag || lastScope.closed) {
                        scopes.push({
                            type: type,
                            tag: tag,
                            closed: closed,
                            children: []
                        });
                    }
                    if (closed) {
                        popLastScope();
                    }
                }
                else if (scopes.length > 0 && type == scopes[scopes.length - 1].type) {
                    popLastScope();
                }
                else {
                    scopes.push({
                        type: type,
                        children: []
                    });
                }
            }
        }
        const tail = chunk.substring(lastIndex);
        if (tail.length > 0) {
            addNode(cleanTextNode(tail));
        }
    }
    let remaining;
    while ((remaining = scopes.shift()) !== undefined) {
        if (remaining.type !== "xml") {
            nodes.push(MARKUP_TO_TOKEN[remaining.type]);
            for (const child of remaining.children) {
                nodes.push(child);
            }
        }
        else {
            nodes.push(remaining);
        }
    }
    return nodes;
};
export const parseMarkdownText = (text) => {
    return parseMarkdownGeneric([text], (t) => t, (t, x) => x);
};
class AbstractMarkdownContext {
    node;
    constructor(node) {
        this.node = node;
    }
    parse(line, parser) {
        return parser.parse(line);
    }
    updateRejected(node) {
        return node;
    }
}
class RootMarkdownContext extends AbstractMarkdownContext {
    child = undefined;
    parse(line, parser) {
        if (this.child === undefined) {
            return super.parse(line, parser);
        }
        return this.child.parse(line, parser);
    }
    feedRoot(node) {
        if (node.type == "li") {
            node = ListMarkdownContext.listFromNode(node);
            this.child = new ListMarkdownContext(node);
        }
        else if (node.type == "tr") {
            node = {
                type: "table",
                children: [node]
            };
            this.child = new TableMarkdownContext(node);
        }
        else if (node.type == "code_block") {
            this.child = new CodeBlockMarkdownContext(node);
        }
        else if (node.type == "blockquote") {
            const children = node.children;
            const emptyBlock = {
                type: "blockquote",
                indent: node.indent,
                children: []
            };
            const childContext = new BlockquoteMarkdownContext(emptyBlock);
            this.node.children.push(emptyBlock);
            for (const child of children) {
                if (!childContext.feedRoot(child)) {
                    return false;
                }
            }
            this.child = childContext;
            return true;
        }
        else if (node.type == "xml") {
            this.child = new XMLBlockMarkdownContext(node);
        }
        else if (node.type == "p" && node.children.length === 1) {
            const child = node.children[0];
            if (typeof child === "string" &&
                child.length > 3 &&
                child.startsWith("=") &&
                child.endsWith("=") &&
                child.replace(/=/g, "").length == 0 &&
                this.node.children.length > 0) {
                const previous = this.node.children[this.node.children.length - 1];
                if (typeof previous === "string" || (previous.type === "p" && previous.children.length == 1)) {
                    this.node.children[this.node.children.length - 1] = {
                        type: "h1",
                        children: typeof previous === "string" ? [previous] : previous.children
                    };
                    return true;
                }
            }
            if (typeof child === "string" &&
                child.length > 3 &&
                child.startsWith("-") &&
                child.endsWith("/-") &&
                child.replace(/-/g, "").length == 0 &&
                this.node.children.length > 0) {
                const previous = this.node.children[this.node.children.length - 1];
                if (typeof previous === "string" || (previous.type === "p" && previous.children.length == 1)) {
                    this.node.children[this.node.children.length - 1] = {
                        type: "h2",
                        children: typeof previous === "string" ? [previous] : previous.children
                    };
                    return true;
                }
            }
        }
        this.node.children.push(node);
        return true;
    }
    handleRejection(node) {
        if (this.child !== undefined) {
            node = this.child.updateRejected(node);
        }
        this.child = undefined;
        return node;
    }
    feed(node) {
        if (this.child !== undefined) {
            const accepted = this.child.feed(node);
            if (accepted) {
                return accepted;
            }
            node = this.handleRejection(node);
        }
        return this.feedRoot(node);
    }
}
class BlockquoteMarkdownContext extends RootMarkdownContext {
    feed(node) {
        if (this.child !== undefined) {
            let accepted = true;
            if (node.type == "blockquote") {
                if (this.child instanceof BlockquoteMarkdownContext) {
                    accepted = this.child.feed(node);
                }
                else {
                    for (const child of node.children) {
                        if (!this.child.feed(child)) {
                            accepted = false;
                            break;
                        }
                    }
                }
            }
            else {
                accepted = this.child.feed(node);
            }
            if (accepted) {
                return accepted;
            }
            this.child = undefined;
        }
        if (node.type != "blockquote") {
            return false;
        }
        if (this.node.indent < node.indent) {
            return super.feed(node);
        }
        else if (this.node.indent > node.indent) {
            return false;
        }
        else {
            for (const child of node.children) {
                if (!super.feed(child)) {
                    return false;
                }
            }
        }
        return true;
    }
}
class ListMarkdownContext extends AbstractMarkdownContext {
    child = undefined;
    feed(node) {
        if (this.child !== undefined) {
            const accepted = this.child.feed(node);
            if (accepted) {
                return accepted;
            }
            this.child = undefined;
        }
        if (node.type != "li" && node.type != "ul" && node.type != "ol") {
            return false;
        }
        const first = this.node.children[0];
        if (first === undefined || first.type != "li" || node.type != "li") {
            this.node.children.push(node);
        }
        else if (first.indent < node.indent) {
            const list = ListMarkdownContext.listFromNode(node);
            this.child = new ListMarkdownContext(list);
            this.node.children.push(list);
        }
        else if (first.indent > node.indent || isOrdered(first) !== isOrdered(node)) {
            return false;
        }
        else {
            this.node.children.push(node);
        }
        return true;
    }
    static listFromNode(node) {
        return {
            type: isOrdered(node) ? "ol" : "ul",
            children: [node]
        };
    }
}
class TableMarkdownContext extends AbstractMarkdownContext {
    feed(node) {
        if (node.type !== "tr") {
            return false;
        }
        if (this.node.children.length == 0) {
            this.node.children.push(node);
        }
        else if (this.node.children.length == 1) {
            let isHeaderLine = true;
            for (let i = 0; i < node.children.length && isHeaderLine; i++) {
                for (let j = 0; j < node.children[i].children.length && isHeaderLine; j++) {
                    const c = node.children[i].children[j];
                    isHeaderLine = typeof c === "string" && /[^=]/.test(c.trim());
                }
            }
            if (isHeaderLine) {
                this.node.children = [
                    {
                        type: "thead",
                        children: [
                            {
                                type: "tr",
                                children: this.node.children[0].children.map((c) => ({
                                    type: "th",
                                    children: c.children
                                }))
                            }
                        ]
                    },
                    { type: "tbody", children: [] }
                ];
            }
            else {
                this.node.children.push(node);
            }
        }
        else if (this.node.children[1].type == "tbody") {
            this.node.children[1].children.push(node);
        }
        else if (this.node.children.length != 2) {
            this.node.children.push(node);
        }
        return true;
    }
}
function parseRawWithoutFormatting(line, getText = () => undefined) {
    return line
        .map((l) => (typeof l === "string" ? l : getText(l)))
        .filter((x) => x !== undefined)
        .filter((x) => x.length > 0)
        .map(stripAnsi);
}
class CodeBlockMarkdownContext extends AbstractMarkdownContext {
    isOpen = true;
    feed(node) {
        if (!this.isOpen) {
            return false;
        }
        if (node.type != "p") {
            this.isOpen = node.type != "code_block";
            if (node.type === "code_block" && node.children.filter((x) => x.length > 0).length === 0) {
                return true;
            }
            return false;
        }
        const text = node.children.filter((c) => typeof c === "string");
        if (text.length != node.children.length) {
            return false;
        }
        this.node.children = this.node.children.concat(text);
        return true;
    }
    updateRejected(node) {
        if (node.type !== "code_block" || node.children.length === 0) {
            return node;
        }
        const children = node.children.slice();
        if (node.language !== undefined) {
            children[0] = node.language + " " + children[0];
        }
        return {
            type: "p",
            children: children
        };
    }
    parse(line, parser) {
        const parsed = parser.parse(line);
        if (parsed.type == "code_block" || !this.isOpen) {
            return parsed;
        }
        return {
            type: "p",
            children: parseRawWithoutFormatting(line, parser.getText)
        };
    }
}
class XMLBlockMarkdownContext extends RootMarkdownContext {
    feed(node) {
        const canAccept = !(this.node.closed || (node.type === "xml" && node.closed && node.tag == this.node.tag));
        if (canAccept) {
            this.node.children.push(node);
            return true;
        }
        this.node.closed = true;
        node = this.handleRejection(node);
        return node.type === "xml" && node.closed === true && node.children.length == 0;
    }
    updateRejected(node) {
        if (node.type !== "xml" || node.children.length === 0) {
            return node;
        }
        return {
            type: "fragment",
            children: node.children
        };
    }
}
const HASH_COUNT_TYPE_MAP = {
    1: "h1",
    2: "h2",
    3: "h3",
    4: "h4",
    5: "h5",
    6: "h6"
};
class MarkdownParser {
    getText;
    updateText;
    constructor(getText, updateText) {
        this.getText = getText;
        this.updateText = updateText;
    }
    getHead(line) {
        if (line.length === 0) {
            return "";
        }
        else if (typeof line[0] === "string") {
            return line[0];
        }
        const head = this.getText(line[0]);
        if (head === undefined) {
            return "";
        }
        return head;
    }
    getChildren(head, line, prefixLength) {
        if (prefixLength !== undefined && prefixLength > 0) {
            line = line.slice();
            const childHead = head.substring(prefixLength);
            if (typeof line[0] === "string") {
                line[0] = childHead;
            }
            else {
                line[0] = this.updateText(line[0], childHead);
            }
        }
        return parseMarkdownLine(line);
    }
    parse(line) {
        const headStr = this.getHead(line);
        let i = 0, j = 0;
        if (headStr.length == 0) {
            return {
                type: "p",
                children: this.getChildren(headStr, line)
            };
        }
        else if (typeof line[0] === "string" && line[0].trimStart().startsWith("|")) {
            const last = line[line.length - 1];
            if (typeof last === "string" && last.trimEnd().endsWith("|")) {
                const divisions = [];
                const splitLine = [];
                for (i = 0; i < line.length; i++) {
                    let l = line[i];
                    if (typeof l === "string") {
                        if (i == line.length - 1) {
                            l = l.trimEnd();
                            l = l.substring(0, l.length - 1);
                        }
                        const split = l.trim().split("|");
                        if (split.length > 0) {
                            if (splitLine.length > 0) {
                                const firstSplit = split[0].trim();
                                if (firstSplit.length > 0) {
                                    splitLine[splitLine.length - 1].push(firstSplit);
                                }
                            }
                            for (j = 1; j < split.length; j++) {
                                const s = split[j].trim();
                                splitLine.push(s == "" ? [] : [s]);
                            }
                        }
                    }
                    else if (splitLine.length > 0) {
                        splitLine[splitLine.length - 1].push(l);
                    }
                }
                // eslint-disable-next-line @typescript-eslint/prefer-for-of
                for (i = 0; i < splitLine.length; i++) {
                    divisions.push({
                        type: "td",
                        children: parseMarkdownLine(splitLine[i])
                    });
                }
                if (divisions.length > 1) {
                    return {
                        type: "tr",
                        children: divisions
                    };
                }
            }
        }
        let indent = 0;
        let hashCount = 0;
        let tickCount = 0;
        let numericPrefix = "";
        let isBullet = false;
        let isBlockQuote = false;
        let prefixLength = 0;
        for (i = 0; i < headStr.length; i++) {
            const char = headStr[i];
            if ((i == 0 && char == "-") || char == "*" || char == "_") {
                for (let j = 1; j < headStr.length; j++) {
                    if (char != headStr[j]) {
                        break;
                    }
                    if (j == headStr.length - 1 && j > 1) {
                        return {
                            type: "hr",
                            children: []
                        };
                    }
                }
            }
            if (/\s/.test(char)) {
                if (i == indent) {
                    indent += 1;
                }
                else {
                    prefixLength = i + 1;
                    break;
                }
            }
            else if (i == hashCount && char == "#" && hashCount < 6) {
                hashCount += 1;
            }
            else if (/[0-9]/.test(char)) {
                numericPrefix += char;
            }
            else if (numericPrefix.length > 0) {
                if (char != ".") {
                    numericPrefix = "";
                    break;
                }
            }
            else if (char == ">") {
                if (headStr[i + 1] === ">" || (/\s/.test(headStr[i + 1]) && headStr[i + 2] === ">")) {
                    indent += 1;
                    continue;
                }
                else if (headStr[i + 1] === undefined) {
                    isBlockQuote = true;
                    prefixLength = i + 1;
                    break;
                }
                else if (/\s/.test(headStr[i + 1]) && headStr[i + 2] !== ">") {
                    isBlockQuote = true;
                    prefixLength = i + 2;
                    if (headStr[i + 2] !== ">") {
                        break;
                    }
                }
                else {
                    prefixLength = 0;
                    break;
                }
            }
            else if (char == "-" || char == "*" || char == "+") {
                if (i < headStr.length - 1) {
                    const next = headStr[i + 1];
                    if (/\s/.test(next)) {
                        isBullet = true;
                        prefixLength = i + 2;
                        break;
                    }
                    else {
                        prefixLength = 0;
                        break;
                    }
                }
            }
            else if (char == "`" && tickCount == prefixLength && tickCount < 3) {
                tickCount += 1;
                prefixLength += 1;
            }
            else if (tickCount === 3 && char != "`") {
                prefixLength += 1;
            }
            else {
                prefixLength = 0;
                break;
            }
        }
        if (prefixLength == 0) {
            const children = this.getChildren(headStr, line);
            if (children.length === 1) {
                const head = children[0];
                if (typeof head !== "string" && head.type === "xml") {
                    return head;
                }
            }
            return {
                type: "p",
                children: this.getChildren(headStr, line)
            };
        }
        else if (HASH_COUNT_TYPE_MAP[hashCount] !== undefined) {
            return {
                type: HASH_COUNT_TYPE_MAP[hashCount],
                children: this.getChildren(headStr, line, prefixLength)
            };
        }
        else if (numericPrefix) {
            return {
                type: "li",
                indent: indent,
                value: Number(numericPrefix),
                children: this.getChildren(headStr, line, prefixLength)
            };
        }
        else if (isBlockQuote) {
            if (prefixLength !== undefined && prefixLength > 0) {
                line = line.slice();
                const childHead = headStr.substring(prefixLength);
                if (typeof line[0] === "string") {
                    line[0] = childHead;
                }
                else {
                    line[0] = this.updateText(line[0], childHead);
                }
            }
            return {
                type: "blockquote",
                indent: indent,
                children: [this.parse(line)]
            };
        }
        else if (isBullet) {
            return {
                type: "li",
                indent: indent,
                children: this.getChildren(headStr, line, prefixLength)
            };
        }
        else if (tickCount == 3) {
            const adjustedPrefixLength = prefixLength + indent;
            const language = tickCount == adjustedPrefixLength ? undefined : headStr.substring(indent + tickCount, adjustedPrefixLength);
            line = line.slice();
            const childHead = headStr.substring(adjustedPrefixLength);
            if (typeof line[0] === "string") {
                line[0] = childHead;
            }
            else {
                line[0] = this.updateText(line[0], childHead);
            }
            return {
                type: "code_block",
                language: language,
                children: parseRawWithoutFormatting(line, this.getText)
            };
        }
        return {
            type: "p",
            children: this.getChildren(headStr, line)
        };
    }
    parseLines(lines) {
        const results = [];
        const context = new RootMarkdownContext({
            type: "fragment",
            children: results
        });
        for (let i = 0; i < lines.length; i++) {
            context.feed(context.parse(lines[i], this));
        }
        return results;
    }
}
export const parseMarkdownGeneric = (content, getText, updateText, splitLines) => {
    const lines = [];
    let line = [];
    const addLine = (line) => {
        lines.push(line
            .filter((l) => (typeof l === "string" && l.length > 0) || (typeof l !== "string" && (getText(l) ?? "").length > 0))
            .reduce((l, part) => {
            if (typeof l[l.length - 1] === "string" && typeof part === "string") {
                l[l.length - 1] = l[l.length - 1] + part;
            }
            else {
                l.push(part);
            }
            return l;
        }, []));
    };
    const objectToLineChunk = (obj, text) => {
        if (text.length == 0 || text == "-" || text == "*") {
            return text;
        }
        return updateText(obj, text);
    };
    const splitLinesFunc = splitLines !== undefined ? splitLines : (t) => getText(t)?.split("\n") ?? [];
    for (const chunk of content) {
        if (typeof chunk !== "string") {
            const text = getText(chunk);
            if (text === undefined) {
                line.push(chunk);
                continue;
            }
            const split = splitLinesFunc(chunk);
            line.push(objectToLineChunk(chunk, split[0]));
            if (split.length == 1) {
                continue;
            }
            addLine(line);
            line = [objectToLineChunk(chunk, split[split.length - 1])];
            if (split.length > 2) {
                split.slice(1, split.length - 1).forEach((l) => addLine([objectToLineChunk(chunk, l)]));
            }
            continue;
        }
        const chunkLines = chunk.split("\n");
        line.push(chunkLines[0]);
        if (chunkLines.length == 1) {
            continue;
        }
        addLine(line);
        line = [chunkLines[chunkLines.length - 1]];
        if (chunkLines.length > 2) {
            chunkLines.slice(1, chunkLines.length - 1).forEach((l) => addLine([l]));
        }
    }
    addLine(line);
    const parser = new MarkdownParser(getText, updateText);
    const parsedLines = parser.parseLines(lines);
    if (parsedLines.length > 0) {
        const tail = parsedLines[parsedLines.length - 1];
        if (tail.type == "p" && tail.children.length == 0) {
            parsedLines.pop();
        }
    }
    return parsedLines;
};
